import { onMounted, onUnmounted } from "vue";
import { getAvailability } from "@/api/api";
import { useBookingDataStore } from "@/stores/bookingData";
import { useBookingStepStore } from "@/stores/bookingStep";
import { Mode } from "@/api/types/mode.enum";
import { Steps } from "@/api/types/steps.enum";
import { formatDateGSheet } from "@/utils.ts/date";

// How often the selected date's availability is re-verified while the wizard is
// open. The authoritative double-booking catch stays server-side at checkout;
// this is the early, friendly heads-up so a customer isn't surprised only after
// filling the whole form.
export const AVAILABILITY_POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

/**
 * Whether `date` is still bookable for `venue`. Mirrors the checkout 409 logic:
 * the availability query is venue-scoped, so a date whose chosen venue is taken
 * reads as unavailable even if the other venue is free — and with no venue yet,
 * only a fully-booked date reads as unavailable (it drops out of the payload).
 *
 * Fail-soft: an unsuccessful/malformed response is treated as still-available so
 * a transient network blip never yanks a customer off their date — checkout
 * re-checks authoritatively before taking payment.
 */
export async function isSelectedDateAvailable(
  date: Date,
  venue: string | undefined,
  fetchAvailability: typeof getAvailability = getAvailability,
): Promise<boolean> {
  const res = await fetchAvailability(date.getFullYear(), date.getMonth() + 1, venue || undefined);
  if (!res?.success || !Array.isArray(res.data)) return true;
  const target = formatDateGSheet(date);
  return res.data.some(
    (entry: unknown) =>
      (typeof entry === "string" ? entry : (entry as { date?: string })?.date) === target,
  );
}

export interface DateAvailabilityWatcherOptions {
  /** Gate: only check when the wizard is in a state where the date matters. */
  shouldWatch: () => boolean;
  getDate: () => Date | undefined;
  getVenue: () => string | undefined;
  /** Called when a still-selected date is found to be no longer available. */
  onDateLost: () => void;
  fetchAvailability?: typeof getAvailability;
  intervalMs?: number;
}

/**
 * Framework-free core of the watcher: a poll interval plus tab-refocus
 * listeners that call `onDateLost` when the currently-selected date is no
 * longer free. Extracted from the composable so it can be unit-tested without
 * mounting a component. Safe to construct in a non-DOM environment — the
 * `document`/`window` accesses are all guarded.
 */
export function createDateAvailabilityWatcher(opts: DateAvailabilityWatcherOptions) {
  const intervalMs = opts.intervalMs ?? AVAILABILITY_POLL_INTERVAL_MS;
  const fetchAvailability = opts.fetchAvailability ?? getAvailability;
  let timerId: ReturnType<typeof setInterval> | null = null;
  let checking = false;
  let stopped = false;
  // The date we last reported lost, so we neither re-fire nor keep polling
  // while the "Date indisponible" modal is open. Cleared once the selected date
  // changes (or is cleared).
  let notifiedDate: Date | undefined = undefined;

  async function check(): Promise<void> {
    // One in-flight check at a time; skip when the gate is closed or no date is
    // selected.
    if (stopped || checking || !opts.shouldWatch()) return;
    const date = opts.getDate();
    if (!date) {
      notifiedDate = undefined;
      return;
    }
    // Already told the customer this exact date is gone — don't keep polling or
    // re-firing until they pick a different one.
    if (notifiedDate === date) return;
    const venue = opts.getVenue();
    checking = true;
    try {
      const available = await isSelectedDateAvailable(date, venue, fetchAvailability);
      // Re-validate after the await: the customer may have navigated, cleared,
      // or changed the date OR the venue while the request was in flight (the
      // result was computed for that old date + venue).
      if (
        !available &&
        !stopped &&
        opts.shouldWatch() &&
        opts.getDate() === date &&
        opts.getVenue() === venue
      ) {
        notifiedDate = date;
        opts.onDateLost();
      }
    } finally {
      checking = false;
    }
  }

  function onVisibilityChange(): void {
    if (typeof document === "undefined" || document.visibilityState === "visible") void check();
  }
  function onFocus(): void {
    void check();
  }

  function start(): void {
    stopped = false;
    timerId = setInterval(() => void check(), intervalMs);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibilityChange);
    if (typeof window !== "undefined") window.addEventListener("focus", onFocus);
  }

  function stop(): void {
    stopped = true;
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibilityChange);
    if (typeof window !== "undefined") window.removeEventListener("focus", onFocus);
  }

  return { start, stop, check };
}

/**
 * Whether the wizard is in a state where the selected date must be watched: the
 * normal flow, past date selection, and before the payment/terminal screens.
 *
 * - Date step is excluded — its calendar is already freshly loaded.
 * - Summary is excluded — there the server-side checkout 409 is the
 *   authoritative date-conflict path and step-summary owns that UX, so watching
 *   it would only duplicate the modal and race the Stripe redirect.
 * - Menu/Noon modes are excluded — their date is fixed (a confirmed booking or
 *   an invite reservation), not a live-contended selection.
 */
export function isWatchableState(mode: Mode, step: Steps): boolean {
  if (mode !== Mode.Normal) return false;
  return (
    step !== Steps.Date &&
    step !== Steps.Summary &&
    step !== Steps.SummaryMenu &&
    step !== Steps.Confirmation
  );
}

/**
 * Wire the date-availability watcher to the booking stores and the component
 * lifecycle. See {@link isWatchableState} for exactly when it is active.
 */
export function useDateAvailabilityWatch(onDateLost: () => void) {
  const bookingData = useBookingDataStore();
  const step = useBookingStepStore();

  const watcher = createDateAvailabilityWatcher({
    shouldWatch: () => isWatchableState(step.getMode(), step.step),
    getDate: () => bookingData.date,
    getVenue: () => bookingData.venue || undefined,
    onDateLost,
  });

  onMounted(() => watcher.start());
  onUnmounted(() => watcher.stop());

  return watcher;
}
